import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { BillEstimate, Shifts, Timetable, Timetables } from "domain/core/types";
import moment from "moment";
import React, { useCallback } from "react";
import { styled } from "styled-components";
import { PADDING } from "ui/theme";

interface CurrentChargesProps {
  billEstimate?: BillEstimate;
  GSTInclusive: boolean;
}

const SpacerSm = styled.div`
  height: ${PADDING.small};
`;

const Container = styled.div`
  padding: 0;
`;

export const CurrentCharges: React.FC<CurrentChargesProps> = ({
  billEstimate,
  GSTInclusive,
}) => {
  const usageRateDetails = billEstimate?.usage_rates;

  const getAdjustedToCentsGSTPrice = useCallback(
    (value: string) => {
      if (GSTInclusive) {
        return (parseFloat(value) * 100 * 1.15).toFixed(2);
      }
      return (parseFloat(value) * 100).toFixed(2);
    },
    [GSTInclusive],
  );

  const getMonthsPrefix = useCallback((timetable: Timetable) => {
    if (timetable.first_month !== null && timetable.last_month !== null) {
      return `${timetable.first_month} - ${timetable.last_month}, `;
    }
    return "";
  }, []);

  const getDaysPrefix = useCallback((timetable: Timetable) => {
    if (timetable.weekends && timetable.holidays) {
      return "Weekends & public holidays";
    }
    if (timetable.weekends) {
      return "Weekends";
    }
    if (timetable.holidays) {
      return "Public holidays";
    }

    return "Weekdays";
  }, []);

  const getShifts = useCallback((shifts: Shifts[]) => {
    return shifts.map((shift, index) => {
      if (shift.start_at === "00:00" && shift.end_at === "00:00") {
        return "Anytime";
      }

      const start = moment(shift.start_at, "HH:mm").format("h:mma");
      const end = moment(shift.end_at, "HH:mm").format("h:mma");

      if (index === shifts.length - 1) {
        return `${start} - ${end}`;
      }
      return `${start} - ${end} & `;
    });
  }, []);

  const renderTimeTable = useCallback(
    (timetables?: Timetables) => {
      return timetables?.timetable?.map((tt, index) => {
        const month = getMonthsPrefix(tt);
        const days = getDaysPrefix(tt);
        const shifts = getShifts(tt.shifts);

        return (
          <div key={`rate-card-timetable-${index}`}>
            <Typography variant="caption">
              {month}
              {days}: {shifts}
            </Typography>
          </div>
        );
      });
    },
    [getMonthsPrefix, getDaysPrefix, getShifts],
  );

  const channelCodesToString = (channels: Array<any>) => {
    const channelNames = Array.from(
      new Set(channels.map((channel) => channel.content_name)),
    );
    return channelNames.join(", ");
  };

  const supplyCharges = billEstimate?.subscription_rates[0];

  return (
    <Container>
      <TableContainer>
        <Table sx={{ minWidth: 300 }} aria-label="rates-table">
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle1" component="p">
                  Current charges
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle1" component="p">
                  Rate
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usageRateDetails?.map((usageRate, index) => {
              const timetableForRate = billEstimate?.timetables[index];
              const channelNames = channelCodesToString(usageRate.channels);

              const rateTimetables = billEstimate?.timetables;

              return (
                <>
                  {index !== 0 && <SpacerSm />}
                  {usageRate?.rates.map((rate, rateIndex) => (
                    <TableRow
                      key={rateIndex}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        <Typography variant="caption" component="p">
                          {rate.rate_code}
                          {rateTimetables[rateIndex] && (
                            <div key={`rateRate-timetable-${rateIndex}`}>
                              <Typography variant="caption" component="p">
                                {rateTimetables[rateIndex]?.descriptions}
                              </Typography>
                              {timetableForRate &&
                                renderTimeTable(timetableForRate)}{" "}
                            </div>
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography>
                          {channelNames}:{" "}
                          {getAdjustedToCentsGSTPrice(rate.rate)} c/kWh
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              );
            })}
            {supplyCharges && (
              <TableRow>
                <TableCell component="th" scope="row">
                  <Typography variant="caption" component="p">
                    Daily charges
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="caption" component="p">
                    {getAdjustedToCentsGSTPrice(supplyCharges.rates[0].rate)}{" "}
                    c/day
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            <TableRow
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
            >
              <TableCell />
              <TableCell component="th" scope="row" align="right">
                <Typography variant="caption" component="p">
                  GST {GSTInclusive ? "inclusive" : "exclusive"}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};
